import React, { useState, useEffect, useCallback } from 'react';
import { getGlossary, addGlossaryTerm, deleteGlossaryTerm } from '../services/api';
import '../styles/GlossaryPanel.css';

export default function GlossaryPanel({ paper, user, onClose, currentPage, selectedText }) {
  const [terms, setTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState('');
  const [newTerm, setNewTerm] = useState(selectedText || '');
  const [deletingId, setDeletingId] = useState(null);
  const [generatingFor, setGeneratingFor] = useState(null); // term id being defined

  const loadTerms = useCallback(async () => {
    if (!paper?.id || !user?.id) return;
    setLoading(true);
    setError('');
    try {
      const data = await getGlossary(paper.id, user.id);
      setTerms(data.terms || []);
    } catch {
      setError('Failed to load glossary.');
    } finally {
      setLoading(false);
    }
  }, [paper?.id, user?.id]);

  useEffect(() => { loadTerms(); }, [loadTerms]);
  useEffect(() => { if (selectedText) setNewTerm(selectedText); }, [selectedText]);

  const handleAdd = async (e) => {
    e.preventDefault();
    const term = newTerm.trim();
    if (!term) return;
    setAdding(true);
    setAddError('');
    setGeneratingFor('new');
    try {
      await addGlossaryTerm(user.id, paper.id, term, '', currentPage || null);
      setNewTerm('');
      await loadTerms();
    } catch {
      setAddError('Failed to add term. It may already exist.');
    } finally {
      setAdding(false);
      setGeneratingFor(null);
    }
  };

  const handleDelete = async (termId) => {
    setDeletingId(termId);
    try {
      await deleteGlossaryTerm(termId, user.id);
      setTerms(prev => prev.filter(t => t.id !== termId));
    } catch {
      // silently fail
    } finally {
      setDeletingId(null);
    }
  };

  // Group terms by first letter
  const grouped = terms.reduce((acc, t) => {
    const letter = t.term[0].toUpperCase();
    if (!acc[letter]) acc[letter] = [];
    acc[letter].push(t);
    return acc;
  }, {});
  const letters = Object.keys(grouped).sort();

  return (
    <div className="gp-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="gp-panel">
        <div className="gp-header">
          <span className="gp-title">📖 Glossary</span>
          <button className="gp-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        {/* Add term form */}
        <div className="gp-add-form">
          <form onSubmit={handleAdd} className="gp-add-row">
            <input
              className="gp-add-input"
              type="text"
              placeholder="Add a term…"
              value={newTerm}
              onChange={e => setNewTerm(e.target.value)}
              disabled={adding}
            />
            <button
              className="gp-add-btn"
              type="submit"
              disabled={adding || !newTerm.trim()}
            >
              {adding ? (generatingFor === 'new' ? 'Defining…' : 'Adding…') : '+ Add'}
            </button>
          </form>
          {addError && <p className="gp-add-error">{addError}</p>}
          <p className="gp-add-hint">AI will generate a definition based on this paper.</p>
        </div>

        <div className="gp-body">
          {loading && <p className="gp-status">Loading…</p>}
          {error && <p className="gp-status gp-status--error">{error}</p>}
          {!loading && !error && terms.length === 0 && (
            <p className="gp-status">No terms yet. Add one above or highlight text while reading.</p>
          )}
          {!loading && !error && letters.map(letter => (
            <div key={letter} className="gp-group">
              <div className="gp-group-letter">{letter}</div>
              {grouped[letter].map(t => (
                <div key={t.id} className="gp-term-card">
                  <div className="gp-term-row">
                    <span className="gp-term-name">{t.term}</span>
                    {t.page_number && (
                      <span className="gp-term-page">p.{t.page_number}</span>
                    )}
                    <button
                      className="gp-term-delete"
                      onClick={() => handleDelete(t.id)}
                      disabled={deletingId === t.id}
                      aria-label="Delete term"
                    >
                      {deletingId === t.id ? '…' : '×'}
                    </button>
                  </div>
                  <p className="gp-term-definition">{t.definition || <em className="gp-no-def">No definition yet.</em>}</p>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
